import { DateTime } from "luxon";
import type { DateHour } from "@/models/common";

/**
 * Creates the correct DateHour model based on the current hour in the time range.
 * @param currentHourOffset - the integer index value of the current mapping.
 * @param maxHourOffset - the max integer difference in hours between start and end time.
 * @param startTime - the DateTime value for the start time.
 * @param endTime - the DateTime value for the end time.
 * @returns a Datehour object based on the input parameters
 */
function dateHourModelFromHour(
  currentHourOffset: number,
  maxHourOffset: number,
  startTime: DateTime,
  endTime: DateTime
): DateHour {
  const dateHour: DateHour = {
    dateHour: startTime
      .plus({ hours: currentHourOffset })
      .toUTC()
      .toFormat("yyyy/MM/dd/HH"),
  };

  if (currentHourOffset === 0) {
    dateHour.firstMinute = startTime.minute;
  }

  const zeroIndexMaxHourOffset: number = maxHourOffset - 1;
  if (currentHourOffset === zeroIndexMaxHourOffset) {
    dateHour.lastMinute = endTime.minute;
  }

  return dateHour;
}

/**
 * Creates a list of DateHour models for the hours between the two time ranges.
 * @param startTime - the DateTime value for the start time.
 * @param endTime - the DateTime value for the end time.
 * @returns List of dateHour models between the startTime and endTime datetimes
 */
export default function dateHoursFromRange(
  startTime: DateTime,
  endTime: DateTime
): DateHour[] {
  const { hours = 0 } = endTime
    .endOf("hour")
    .diff(startTime.startOf("hour"), "hours")
    .toObject();
  const maxHourOffset = Math.ceil(hours);

  const results: DateHour[] = [...Array(maxHourOffset)].map((_, i) => {
    return dateHourModelFromHour(i, maxHourOffset, startTime, endTime);
  });

  return results;
}

/**
 * Get the relative time for any range in days, hours or minutes
 * @param startTime - initial time from range
 * @param endTime - final time from range
 */
export function getRelativeTimeFromRange(
  startTime: DateTime,
  endTime: DateTime
): string {
  const threshold = 30; // Threshold time for knowing endTime was local
  const diff = endTime.diff(startTime);

  if (DateTime.now().diff(endTime).as("seconds") < threshold) {
    if (diff.as("days") >= 1) {
      return `${diff.as("days")}d`;
    } else if (diff.as("hours") >= 1) {
      return `${diff.as("hours")}h`;
    } else if (diff.as("minutes") >= 1) {
      return `${diff.as("minutes")}m`;
    }
  }
  return "";
}
